import { useEffect, useState } from "react";
import Modal from "@shared/v2/Modal";
import Typography from "@shared/v2/Typography";
import { XmarkSolidV6 } from "@shared/v2/Icomoon";
import Button from "@shared/v2/Button";
import Dropdown from "@shared/v2/Dropdown";
import TextInput from "@shared/v2/TextInput";
import Alert from "@shared/v2/Alert";
import { useDispatch } from "react-redux";
import { setSnackbar } from "../../reducers/snackbarReducer";

interface FormData {
  churnCode: string;
  churnCodeFeedback: string;
}

interface OpportunitiesCancelModal {
  accountProduct?: { id: string | number };
  onClose: () => void;
  open: boolean;
  onCancel: (
    accountProductId: string | number,
    cancelForm: { status: string; details: FormData },
  ) => Promise<void>;
}

const CANCEL_REASONS = [
  "Our team isn't getting a lot of use out of Opportunities",
  "The product is not intuitive to use",
  "I'm cancelling due to financial constraints",
  "Opportunities is missing features that I need",
  "Opportunities is too difficult to set up",
  "I’d like to use another service for prospecting",
  "I haven’t received enough calls or leads",
  "I had technical issues with this product",
].map((reason) => ({ label: reason, value: reason }));

const OpportunitiesCancelModal: React.FC<OpportunitiesCancelModal> = ({
  accountProduct,
  onClose,
  open,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [churnCode, setChurnCode] = useState<(typeof CANCEL_REASONS)[0] | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) {
      setStep(1);
      setChurnCode(null);
    }
  }, [open]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});
    if (step === 1) {
      setStep(2);
    } else {
      const details = Object.fromEntries(new FormData(e.currentTarget).entries()) as unknown as FormData;
      if (!details.churnCode) {
        setErrors({ churnCode: "Please select a reason for cancelling" });
        return;
      }
      if (!accountProduct?.id) return;
      onCancel(accountProduct.id, { status: "canceled", details })
        .then(() => {
          onClose();
          dispatch(
            setSnackbar({
              text: "Successfully submitted Opportunities cancellation.",
              schema: "executed",
              timeout: 5000,
              position: "top",
              show: true,
              closeOnTimeout: () => {
                dispatch(setSnackbar({ show: false }));
              },
            }),
          );
        })
        .catch(() => {
          setErrors({
            global:
              "An error occurred with cancelling your subscription. Please try again or contact support if this issue persists.",
          });
        });
    }
  };

  return (
    <Modal
      onHide={onClose}
      show={open}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-max-w-[640px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col"
    >
      <form className="tw-flex tw-flex-col tw-gap-[32px] tw-overflow-hidden" onSubmit={handleSubmit}>
        {/* @ts-expect-error props */}
        <Modal.Header
          title={step === 1 ? "Are you sure?" : "Confirm Opportunities Cancellation"}
          closeButton
        />

        <Modal.Body className="tw-flex-1 tw-overflow-y-auto tw-flex tw-flex-col tw-gap-[24px] tw-pb-[16px]">
          {errors.global && (
            <Alert title="An error occurred while cancelling" text={errors.global} variant="error" />
          )}

          {step === 1 && (
            <div className="tw-flex tw-flex-col tw-gap-[8px]">
              {/* @ts-expect-error props */}
              <Typography>
                Opportunities brings thousands of potential leads to your team’s fingertips. If you cancel,
                you’ll lose access to...
              </Typography>
              {/* @ts-expect-error props */}
              <Typography className="tw-flex tw-items-center tw-gap-[4px]">
                <XmarkSolidV6 size="l" className="tw-text-semantic-red-100" /> Expired, FSBO, and
                Pre-Foreclosure data, right in your CRM
              </Typography>
              {/* @ts-expect-error props */}
              <Typography className="tw-flex tw-items-center tw-gap-[4px]">
                <XmarkSolidV6 size="l" className="tw-text-semantic-red-100" /> The ease of calling potential
                owners from those properties in one system
              </Typography>
              {/* @ts-expect-error props */}
              <Typography className="tw-flex tw-items-center tw-gap-[4px]">
                <XmarkSolidV6 size="l" className="tw-text-semantic-red-100" /> A seamless, integrated workflow
                for nurturing your prospecting leads
              </Typography>
            </div>
          )}

          {step === 2 && (
            <>
              {/* @ts-expect-error props */}
              <Typography>
                <b>Your Opportunities access will end at the end of the current billing period.</b> You and
                your team can continue to access Opportunities data and use the feature as you currently do
                until that time.
              </Typography>
              {/* @ts-expect-error props */}
              <Typography className="!tw-italic">
                All cancellations must be done 7 days before your bill date, or you will be charged for the
                following month.
              </Typography>
              {/* @ts-expect-error props */}
              <Typography>
                <b>At the end of the current billing period...</b>
                <ul className="tw-pl-[24px]">
                  <li>
                    You and your team will still retain the leads you added previously from Opportunities
                  </li>
                  <li>
                    We cannot guarantee retaining call history and records for any calls made from
                    Opportunities to potential owners.
                  </li>
                </ul>
              </Typography>
              <Dropdown
                // @ts-expect-error props
                label="Reason for Cancelling"
                isRequired
                name="churnCode"
                helperText="To help us improve Opportunities, please share why you're cancelling"
                error={errors.churnCode}
                options={CANCEL_REASONS}
                value={churnCode}
                onChange={setChurnCode}
                menuShouldComeToFront
              />
              <TextInput
                // @ts-expect-error props
                label="Additional Feedback"
                name="churnCodeFeedback"
                multiline
                rows={4}
                placeholder="How can we make Opportunities better?"
              />
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="tw-flex tw-justify-between tw-gap-[16px]">
          {/* @ts-expect-error props */}
          <Button size="medium" schema={step === 1 ? "secondary" : "tertiary"} onClick={onClose}>
            {step === 1 ? "Stay on Opportunities" : "Keep My Subscription"}
          </Button>
          {/* @ts-expect-error props */}
          <Button type="submit" size="medium" schema={step === 1 ? "tertiary" : "warning"}>
            {step === 1 ? "Continue to Cancel" : "Cancel Opportunities"}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default OpportunitiesCancelModal;
